import * as Core from '../core';

export interface ProvaPracticeTitleOption {
    titleKey: string;
    name: string;
    gameName: string;
    gameId: string;
}

export interface ProvaPracticeStatus {
    available: boolean;
    canStart: boolean;
    message: string;
    titles: ProvaPracticeTitleOption[];
}

export interface ProvaAttemptRubricSummary {
    title: string;
    stepTitle?: string;
    score?: number;
    max?: number;
    strengths?: string;
    weaknesses?: string;
    quote?: string;
    reasoning?: string;
}

export interface ProvaAttemptResultSummary {
    attemptId: string;
    titleKey: string;
    status: string;
    competitiveScore?: number;
    overallScore?: number;
    competitiveStatus?: string;
    integrity?: string;
    feedback?: string;
    feedbackType?: string;
    overallExplanation?: string;
    createdUtc: string;
    completedAtUtc?: string;
    rubrics: ProvaAttemptRubricSummary[];
}

export class ProvaService {
    public static async getStatus(): Promise<ProvaPracticeStatus> {
        const route = Core.API.ServerRoute.forAction('prova', 'status');
        return await Core.API.get(route);
    }

    public static async startAttempt(titleKey: string, returnUrl?: string): Promise<Core.Models.ProvaAttempt> {
        const route = Core.API.ServerRoute.forAction('prova', 'attempts');
        return await Core.API.post(route, {
            titleKey,
            returnUrl: returnUrl || window.location.href,
        });
    }

    /** Re-issues a one-time session URL for an existing attempt (resume). */
    public static async openSession(attemptId: string, returnUrl?: string): Promise<Core.Models.ProvaAttempt> {
        const route = Core.API.ServerRoute.forAction('prova', `attempts/${attemptId}/session`);
        return await Core.API.post(route, {
            returnUrl: returnUrl || window.location.href,
        });
    }

    public static async listMyAttempts(): Promise<Core.Models.ProvaAttempt[]> {
        const route = Core.API.ServerRoute.forAction('prova', 'attempts/me');
        return await Core.API.get(route);
    }

    public static async listAttemptsForUser(userId: string): Promise<Core.Models.ProvaAttempt[]> {
        const route = Core.API.ServerRoute.forAction('users', `${userId}/prova-attempts`);
        return await Core.API.get(route);
    }

    public static async getAttemptResult(attemptId: string): Promise<ProvaAttemptResultSummary> {
        const route = Core.API.ServerRoute.forAction('prova', `attempts/${attemptId}/result`);
        return await Core.API.get(route);
    }

    public static async cancelAttempt(attemptId: string): Promise<void> {
        const route = Core.API.ServerRoute.forAction('prova', `attempts/${attemptId}`);
        await Core.API.delete(route);
    }

    public static async ensureCatalog(): Promise<void> {
        const route = Core.API.ServerRoute.forAction('prova', 'catalog/ensure');
        await Core.API.post(route, {});
    }

    public static async getCatalogGames(): Promise<Core.Models.ProvaCatalogGame[]> {
        const route = Core.API.ServerRoute.forAction('prova', 'catalog/games');
        return await Core.API.get(route);
    }

    public static async getCatalogTitles(gameId?: string): Promise<Core.Models.ProvaCatalogTitle[]> {
        const route = Core.API.ServerRoute.forAction('prova', 'catalog/titles');
        return await Core.API.get(route, undefined, gameId ? { gameId } : undefined);
    }

    public static async importTitles(gameId: string | undefined, csvContent: string): Promise<number> {
        const route = Core.API.ServerRoute.forAction('prova', 'catalog/titles/import');
        return await Core.API.post(route, { gameId, csvContent });
    }

    public static async deleteTitle(id: string): Promise<void> {
        const route = Core.API.ServerRoute.forAction('prova', `catalog/titles/${id}`);
        await Core.API.delete(route);
    }

    public static async deleteAllTitles(gameId: string): Promise<number> {
        const route = Core.API.ServerRoute.forAction('prova', `catalog/games/${gameId}/titles`);
        return await Core.API.delete(route);
    }
}
